/* global runtime */
// Rekkefølge, sletting og redigering av banklinjer.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const bankStatementIsSettledLocal = (statement) => Boolean(statement?.bookedAt || statement?.reconciledAt)
  || ['archived', 'reconciled', 'booked'].includes(String(statement?.status || ''));
const bankStatementStartsLocal = (statement) => String(statement?.periodFrom || statement?.periodTo || '');
const bankStatementSameAccountLocal = (left, right) => String(left?.source || 'business') === String(right?.source || 'business')
  && String(left?.bankAccountId || '') === String(right?.bankAccountId || '');
const earlierUnsettledBankStatementLocal = (state, statement) => {
  if (String(statement?.source || 'business') !== 'business') return null;
  return safeArray(state?.bankStatements)
    .filter((entry) => entry?.id !== statement?.id)
    .filter((entry) => bankStatementSameAccountLocal(entry, statement))
    .filter((entry) => bankStatementStartsLocal(entry) && bankStatementStartsLocal(entry) < bankStatementStartsLocal(statement))
    .filter((entry) => !bankStatementIsSettledLocal(entry))
    .sort((left, right) => bankStatementStartsLocal(left).localeCompare(bankStatementStartsLocal(right)))[0] || null;
};
const laterBankStatementLocal = (state, statement) => safeArray(state?.bankStatements)
  .filter((entry) => entry?.id !== statement?.id)
  .filter((entry) => bankStatementSameAccountLocal(entry, statement))
  .filter((entry) => bankStatementStartsLocal(entry) && bankStatementStartsLocal(entry) > bankStatementStartsLocal(statement))
  .sort((left, right) => bankStatementStartsLocal(right).localeCompare(bankStatementStartsLocal(left)))[0] || null;
const bankStatementRequiresDeleteOrderLocal = (statement) => {
  if (statement?.bookedAt || statement?.reconciledAt || statement?.reopenedFromStatus) return true;
  const source = statement?.source || 'business';
  const lines = safeArray(statement?.lines);
  if (!lines.length) return false;
  return lines.every((line) => {
    const status = getBankLineStatus(line, source);
    return source === 'private'
      ? ['drift', 'ignorer'].includes(status)
      : ['matchet', 'privat'].includes(status);
  });
};

const deleteReceiptInboxEntriesFromState = (state, entryIds = []) => {
  const ids = new Set(safeArray(entryIds).map((id) => String(id || '')).filter(Boolean));
  const reopenedTaskIds = new Set();
  const bankStatements = safeArray(state?.bankStatements).map((statement) => ({
    ...statement,
    lines: safeArray(statement?.lines).map((line) => {
      const linkedDeleted = ids.has(String(line?.receiptInboxEntryId || ''));
      const suggestionDeleted = ids.has(String(line?.aiSuggestedReceiptInboxEntryId || ''));
      const receiptAttachments = safeArray(line.receiptAttachments);
      const nextReceiptAttachments = receiptAttachments.filter((entry) => !ids.has(String(entry?.receiptInboxEntryId || entry?.id || '')));
      const attachmentDeleted = nextReceiptAttachments.length !== receiptAttachments.length;
      if (!linkedDeleted && !suggestionDeleted && !attachmentDeleted) return line;
      const booked = Boolean(line?.matchedEntryId) || ['booked', 'matched'].includes(String(line?.reconciliationStatus || ''));
      const next = { ...line };
      if (attachmentDeleted) {
        if (nextReceiptAttachments.length) next.receiptAttachments = nextReceiptAttachments;
        else delete next.receiptAttachments;
      }
      if (suggestionDeleted) ['aiSuggestedReceiptInboxEntryId', 'aiSuggestedReceiptMatchConfidence', 'aiSuggestedReceiptMatchScore', 'aiSuggestedReceiptMatchReason'].forEach((key) => { delete next[key]; });
      if (linkedDeleted && !booked) {
        const primaryReceipt = nextReceiptAttachments[0];
        if (primaryReceipt) {
          next.attachmentName = primaryReceipt.attachmentName || primaryReceipt.name || 'Bilag';
          next.attachmentId = primaryReceipt.attachmentId || primaryReceipt.receiptInboxEntryId || primaryReceipt.id;
          next.attachmentMimeType = primaryReceipt.attachmentMimeType || primaryReceipt.mimeType;
          next.attachmentDownloadUrl = primaryReceipt.attachmentDownloadUrl || primaryReceipt.downloadUrl || primaryReceipt.file;
          next.receiptInboxEntryId = primaryReceipt.receiptInboxEntryId || primaryReceipt.id;
        } else {
          ['attachmentName', 'attachmentId', 'attachmentMimeType', 'attachmentDownloadUrl', 'receiptInboxEntryId'].forEach((key) => { delete next[key]; });
          if (next.documentationMode === 'receipt') { delete next.documentationMode; delete next.documentationReason; }
        }
        reopenedTaskIds.add(`bank:${statement.id}:${line.id}`);
      }
      if ((suggestionDeleted || attachmentDeleted) && !booked) reopenedTaskIds.add(`bank:${statement.id}:${line.id}`);
      return stripUndefinedDeep(next);
    }),
  }));
  const approvals = { ...(state?.autopilotTaskApprovals || {}) };
  reopenedTaskIds.forEach((taskId) => { delete approvals[taskId]; });
  return {
    ...state,
    receiptInbox: safeArray(state?.receiptInbox).filter((entry) => !ids.has(String(entry?.id || ''))),
    bankStatements,
    autopilotTaskApprovals: approvals,
  };
};

const assertCanBookBankStatementLocal = (statementId, lineId = '') => {
  const state = mergeState(runtime.appState);
  const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
  if (!statement) throw new Error(t('svc.stmtNotFound'));
  // Speiler sperren i `assert_can_book` i functions/bank/bankReconciliationMutationHttp.ts.
  if (window.PlummeBankReconciliationFinish.bankStatementSettled(statement)) {
    throw new Error(t('bank.alreadySettled', 'Bankutskriften er allerede avstemt og arkivert. Åpne den igjen hvis noe må rettes.'));
  }
  if (lineId) { const line = safeArray(statement.lines).find((entry) => entry.id === lineId); if (!line) throw new Error(t('svc.bankLineNotFound')); if (line.reconciliationStatus === 'import-removed' || line.importCorrectionAction === 'removed') throw new Error(t('bank.removedLineCannotBook', 'En feilavlest banklinje som er fjernet fra avstemmingen kan ikke bokføres.')); }
  const blocking = earlierUnsettledBankStatementLocal(state, statement);
  if (blocking) throw new Error(t('bank.previousMustReconcile', 'Forrige bankutskrift må avstemmes først. Du kan kontrollere denne utskriften, men ikke bokføre eller avstemme den ennå.'));
  return statement;
};

const assertCanBookBankStatement = async (statementId, lineId = '') => {
  try {
    const result = await bankReconciliationMutation('assert_can_book', { statementId, lineId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return mapBankStatementToUi(assertCanBookBankStatementLocal(statementId, lineId));
  }
};

const deleteBankStatementLocal = (statementId) => {
  let deleted = false;
  updateState((state) => {
    const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
    if (!statement) throw new Error(t('svc.stmtNotFound'));
    if (String(statement.status || '') !== 'active' || statement.reconciledAt) throw new Error(t('bank.deleteReopenFirst', 'Lukkede bankutskrifter må åpnes igjen før de kan slettes.'));
    if (statement.bookedAt) throw new Error(t('bank.deleteReverseFirst', 'Bokføringen må reverseres før bankutskriften kan slettes.'));
    const later = bankStatementRequiresDeleteOrderLocal(statement) ? laterBankStatementLocal(state, statement) : null;
    if (later) throw new Error(t('bank.deleteNewerFirst', 'Nyere bankutskrifter må slettes først. Bankutskrifter slettes fra nyeste til eldste.'));
    // Bokførte føringer stopper ikke slettingen; koblingen deres løsnes i stedet.
    // Se src/services/bankStatementDeletionRelease.js.
    const release = window.PlummeBankStatementDeletion;
    if (release.bookedMatchGroupBlocking(state, statementId)) throw new Error(t('bank.deleteMatchGroupBlocked', 'Bankutskriften inngår i en bokført matchinggruppe. Reverser gruppen før utskriften slettes.'));
    if (release.approvedMatchGroupBlocking(state, statementId)) throw new Error(t('bank.deleteMatchGroupOffline', 'Bankutskriften inngår i en matchinggruppe. Prøv igjen når du er tilkoblet.'));
    const detachedState = release.detachBankStatementLinks(state, statementId, nowIso);
    const receiptIds = new Set(safeArray(statement.lines).map((line) => line?.receiptInboxEntryId).filter(Boolean));
    deleted = true;
    return {
      ...detachedState,
      bankStatements: safeArray(detachedState.bankStatements).filter((entry) => entry.id !== statementId),
      receiptInbox: safeArray(detachedState.receiptInbox).map((entry) => receiptIds.has(entry.id) && entry.status !== 'booked' && (!entry.bookedAs || entry.bookedAs === 'bank-line') ? {
        ...entry,
        status: bankReleasedReceiptStatus(entry),
        bookedAt: undefined,
        bookedAs: undefined,
        returnedFromDeletedBankStatementAt: nowIso(),
      } : entry),
      bankStatementDeletionHistory: [{
        id: `bank-statement-deletion-${Date.now()}`,
        statementId,
        fileName: statement.fileName || statement.originalFileName,
        source: statement.source || 'business',
        bankAccountId: statement.bankAccountId,
        periodFrom: statement.periodFrom,
        periodTo: statement.periodTo,
        lineCount: safeArray(statement.lines).length,
        deletedAt: nowIso(),
      }, ...safeArray(detachedState.bankStatementDeletionHistory)].slice(0, 100),
    };
  }, true);
  return deleted;
};

const deleteBankStatement = async (statementId) => {
  try {
    const result = await bankReconciliationMutation('delete_statement', { statementId });
    return Boolean(result.deletedStatementId);
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return deleteBankStatementLocal(statementId);
  }
};

const updateBankStatementLineLocal = (statementId, lineId, patch) => {
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) =>
      statement.id === statementId
        ? { ...statement, lines: safeArray(statement.lines).map((line) => line.id === lineId ? { ...line, ...patch } : line) }
        : statement,
    ),
  }), true);
};

// A server mutation is authoritative, but the Firestore listener may deliver
// its snapshot a moment later. Keep the in-memory read model in step with the
// Function response so a sequential update -> book flow never validates the
// previous version of the bank line.
const applyBankStatementMutationSnapshot = (statement) => {
  if (!statement?.id) return null;
  const current = mergeState(runtime.appState);
  runtime.appState = {
    ...current,
    bankStatements: safeArray(current.bankStatements).map((entry) =>
      entry.id === statement.id ? statement : entry,
    ),
  };
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mapBankStatementToUi(statement);
};

const updateBankStatementLine = async (statementId, lineId, patch) => {
  try {
    const result = await bankReconciliationMutation('update_line', { statementId, lineId, patch });
    queueAiLearningLog(() => logBankLineDraftCorrection({ statementId, lineId, patch }), { sourceType: 'bank_line', sourceId: statementId, outcome: 'draft_correction' });
    return result.statement ? applyBankStatementMutationSnapshot(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    updateBankStatementLineLocal(statementId, lineId, patch);
    queueAiLearningLog(() => logBankLineDraftCorrection({ statementId, lineId, patch }), { sourceType: 'bank_line', sourceId: statementId, outcome: 'draft_correction' });
    return null;
  }
};

const disableBankPattern = async (patternKey) => {
  if (!patternKey) throw new Error(t('r1e.lines.patternIdMissing'));
  const result = await bankReconciliationMutation('set_bank_pattern_preference', { patternKey, disabled: true });
  return result.disabled === true;
};

const bankImportCorrectionOriginalForLocal = (line) => line?.importCorrectionOriginal || stripUndefinedDeep({
  date: line?.date,
  description: line?.description,
  note: line?.note,
  amount: line?.amount === undefined ? undefined : num(line.amount),
  reconciliationStatus: line?.reconciliationStatus,
  classification: line?.classification,
});

const updateBankStatementLineImportCorrectionLocal = (statementId, lineId, correction = {}) => {
  const reason = String(correction.reason || '').trim();
  if (reason.length < 3) throw new Error(t('r1e.lines.correctionReasonRequired'));
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) => {
      if (statement.id !== statementId) return statement;
      return {
        ...statement,
        lines: safeArray(statement.lines).map((line) => {
          if (line.id !== lineId) return line;
          if (line.bankMatchGroupId || line.matchedEntryId || ['matched', 'booked'].includes(String(line.reconciliationStatus || ''))) {
            throw new Error(t('r1e.lines.alreadyLinked'));
          }
          const base = {
            importCorrectionAction: correction.action === 'remove' || correction.action === 'removed' ? 'removed' : 'corrected',
            importCorrectionReason: reason,
            importCorrectionAt: nowIso(), importCorrectionByUserId: runtime.user?.uid || undefined,
            importCorrectionOriginal: bankImportCorrectionOriginalForLocal(line),
          };
          if (base.importCorrectionAction === 'removed') {
            return stripUndefinedDeep({ ...line, ...base, classification: 'ignored', reconciliationStatus: 'import-removed' });
          }
          return stripUndefinedDeep({
            ...line,
            ...base,
            amount: correction.amount !== undefined ? num(correction.amount) : line.amount,
            date: correction.date || line.date,
            description: correction.description || line.description,
            note: correction.note !== undefined ? String(correction.note || '').trim() : line.note,
            reconciliationStatus: 'manual-review',
          });
        }),
      };
    }),
  }), true);
};

const updateBankStatementLineImportCorrection = async (statementId, lineId, correction = {}) => {
  try {
    const result = await bankReconciliationMutation('correct_import_line', { statementId, lineId, correction });
    queueAiLearningLog(() => logBankLineDraftCorrection({ statementId, lineId, patch: correction }), { sourceType: 'bank_line', sourceId: statementId, outcome: 'import_correction' });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    updateBankStatementLineImportCorrectionLocal(statementId, lineId, correction);
    queueAiLearningLog(() => logBankLineDraftCorrection({ statementId, lineId, patch: correction }), { sourceType: 'bank_line', sourceId: statementId, outcome: 'import_correction' });
    return null;
  }
};

const addBankStatementLineLocal = (statementId, line) => {
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) =>
      statement.id === statementId
        ? { ...statement, lines: [...safeArray(statement.lines), line] }
        : statement,
    ),
  }), true);
};

const addBankStatementLine = async (statementId, line) => {
  try {
    const result = await bankReconciliationMutation('add_line', { statementId, line });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    addBankStatementLineLocal(statementId, line);
    return null;
  }
};

const updateBankStatementBalancesLocal = (statementId, patch = {}) => {
  const nextPatch = {};
  ['openingBalance', 'closingBalance'].forEach((key) => {
    if (!(key in patch)) return;
    const value = window.PlummeMoneyInput.parse(patch[key]);
    if (value != null) nextPatch[key] = value;
  });
  if ('balanceCorrectionReason' in patch) nextPatch.balanceCorrectionReason = String(patch.balanceCorrectionReason || '').trim();
  if (!Object.keys(nextPatch).length) return null;
  let updatedStatement = null;
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) => {
      if (statement.id !== statementId) return statement;
      updatedStatement = {
        ...statement,
        ...nextPatch,
        importedClosingBalance: statement.importedClosingBalance ?? (
          !statement.balanceAdjustedAt && statement.closingBalance !== undefined
            ? num(statement.closingBalance)
            : undefined
        ),
        balanceAdjustedAt: nowIso(),
        balanceCorrectionAt: nextPatch.balanceCorrectionReason ? nowIso() : statement.balanceCorrectionAt,
      };
      return updatedStatement;
    }),
  }), true);
  return updatedStatement ? mapBankStatementToUi(updatedStatement) : null;
};

const updateBankStatementBalances = async (statementId, patch = {}) => {
  try {
    const result = await bankReconciliationMutation('update_balances', { statementId, patch });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return updateBankStatementBalancesLocal(statementId, patch);
  }
};

const suggestBankStatementLines = async (statementId, options = {}) => {
  const state = mergeState(runtime.appState);
  const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
  if (!statement) throw new Error(t('svc.stmtNotFound'));
  const source = statement.source || 'business';
  const allowedStatuses = options.rulesOnly === true ? ['ny', 'forslag', 'kontroll'] : ['ny', 'forslag'];
  const lines = safeArray(statement.lines).filter((line) => {
    const status = getBankLineStatus(line, source);
    return allowedStatuses.includes(status)
      && line.reconciliationStatus !== 'manual-review'
      && line.classification !== 'private'
      && line.classification !== 'ignored';
  });
  if (!lines.length) return [];
  const chartAccounts = mergePlumme1ChartAccounts(state.chartAccounts, state.settings);
  const toSuggestionAccount = (account) => ({
    code: String(account.code || account.accountNumber || '').trim(),
    name: account.name || account.label || `Konto ${account.code || account.accountNumber || ''}`,
  });
  const costAccounts = chartAccounts.filter((account) => /^[4-8]/.test(String(account.code || account.accountNumber || ''))).map(toSuggestionAccount);
  const salesAccounts = chartAccounts.filter((account) => String(account.code || account.accountNumber || '').startsWith('3')).map(toSuggestionAccount);
  const country = String(state.settings?.country || 'NO').toUpperCase();
  const locale = uiLocale();
  const payload = await authorizedPost('suggestBankStatementLinesHttp', {
    businessId: runtime.activeBusinessId || undefined,
    statementId,
    companyTypeCode: state.settings?.companyTypeCode || state.settings?.companyType || '',
    country,
    locale,
    source,
    vatRegistered: state.settings?.vatRegistered !== false,
    standardVat: num(state.settings?.standardVatRate, standardVatForCountry(country)),
    lines: lines.map((line) => ({
      id: line.id,
      date: line.date || todayIso(),
      description: line.description || '',
      amount: num(line.amount),
    })),
    costAccounts,
    salesAccounts,
    rulesOnly: options.rulesOnly === true,
  });
  const suggestions = safeArray(payload.suggestions).filter((suggestion) => suggestion?.lineId);
  if (!suggestions.length) {
    if (options.rulesOnly !== true) await bankReconciliationMutation('update_balances', { statementId, patch: {} });
    return [];
  }
  // Ett kall for hele utskriften. Ett `update_line` per forslag ga en rundtur og
  // en full state-nedlasting per linje, og laa paa kritisk vei naar en faktura ble
  // opprettet: ti forslag ble til ti sekunders venting foer kvitteringen kom.
  await bankReconciliationMutation('apply_line_suggestions', {
    statementId,
    suggestions: suggestions.map((suggestion) => ({
      lineId: suggestion.lineId,
      patch: {
        aiSuggestedClassification: suggestion.classification,
        aiSuggestedBookingType: suggestion.bookingType && suggestion.bookingType !== 'unknown' ? suggestion.bookingType : undefined,
        aiSuggestedAccountCode: suggestion.accountCode || undefined,
        aiSuggestedFromAccountCode: suggestion.fromAccountCode || undefined,
        aiSuggestedToAccountCode: suggestion.toAccountCode || undefined,
        aiSuggestedVatRate: suggestion.vatRate ?? undefined,
        aiSuggestedReverseChargeCode: suggestion.reverseChargeCode || undefined,
        aiSuggestedReason: suggestion.reason || (suggestion.confidence ? `SuperPlumma-forslag (${suggestion.confidence})` : 'SuperPlumma-forslag'),
        bankPatternKey: suggestion.bankPatternKey || undefined,
        bankPatternVariableAmounts: suggestion.bankPatternVariableAmounts === true ? true : undefined,
        aiSuggestionConfidence: suggestion.confidence || undefined,
        aiSuggestedInvoiceId: suggestion.invoiceId || undefined,
        aiSuggestedReceiptInboxEntryId: suggestion.receiptInboxEntryId || undefined,
        aiSuggestedReceiptMatchConfidence: suggestion.receiptMatchConfidence || undefined,
        aiSuggestedReceiptMatchScore: suggestion.receiptMatchScore ?? undefined,
        aiSuggestedReceiptMatchReason: suggestion.receiptMatchReason || undefined,
        documentationMode: suggestion.documentationMode === 'bank-statement' ? 'bank-statement' : undefined,
        documentationReason: suggestion.documentationMode === 'bank-statement' ? suggestion.reason : undefined,
      },
    })),
  });
  await attachHighConfidenceReceiptMatches(statementId);
  return suggestions;
};

// Bilagsmatchen er forhaandsgodkjent, bokforingen er det ikke. Et sikkert treff
// kobles med en gang, slik at kontrollen ligger ett sted — i godkjenningen som
// faktisk bokforer. To kontrollpunkt for det samme bilaget er to klikk for aa si
// ja til den samme vurderingen. Koblingen kan angres: bilaget sendes tilbake til
// innboksen med `detachReceiptFromBankLine`.
// Utvalget er rent, slik at regelen kan testes uten backend: hvilke linjer har et
// sikkert treff som ikke alt er dekket, og hvilket bilag hoerer til hver av dem.
